import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import useStore from './store';

import AppLayout       from './components/Layout/AppLayout';
import LoginPage       from './pages/Auth/LoginPage';
import AuthCallback    from './pages/Auth/AuthCallback';
import Dashboard       from './pages/Dashboard/Dashboard';
import AttendancePage  from './pages/Attendance/AttendancePage';
import LeavesPage      from './pages/Leaves/LeavesPage';
import HolidaysPage    from './pages/Holidays/HolidaysPage';
import UserProfilePage from './pages/UserProfile/UserProfilePage';
import AdminUsersPage  from './pages/Admin/AdminUsersPage';
import AdminLossOfPayReportPage from './pages/Admin/AdminLossOfPayReportPage';
import AdminLeavePoliciesPage from './pages/Admin/AdminLeavePoliciesPage';
import AdminDataSync from './pages/Admin/AdminDataSync';
import AdminLeaveBalanceVerification from './pages/Admin/AdminLeaveBalanceVerification';
import TeamOverviewPage from './pages/Team/TeamOverviewPage';
function LoadingScreen({ message = 'Loading…' }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100vh', background: '#0A0612', flexDirection: 'column', gap: 16,
    }}>
      <div style={{
        width: 44, height: 44,
        border: '3px solid rgba(124,58,237,.25)',
        borderTop: '3px solid #7C3AED',
        borderRadius: '50%',
        animation: 'spin 0.9s linear infinite',
      }} />
      <div style={{ fontSize: '0.875rem', color: 'rgba(159,103,255,.6)' }}>
        {message}
      </div>
      <style>{`@keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }`}</style>
    </div>
  );
}

function RequireAuth({ children, roles }) {
  const { user, token, authLoading } = useStore();
  const location = useLocation();

  if (authLoading) {
    return <LoadingScreen message="Loading your profile…" />;
  }

  if (!token || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AuthInit({ children }) {
  const { token, fetchMe, user } = useStore();

  useEffect(() => {
    if (token && !user) {
      fetchMe();
    } else {
      useStore.setState({ authLoading: false });
    }
  }, []);

  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthInit>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              fontFamily: 'DM Sans, sans-serif',
              fontSize:   '0.875rem',
              borderRadius: '10px',
              boxShadow:  '0 8px 24px rgba(0,0,0,.12)',
            },
            success: { iconTheme: { primary: '#10B981', secondary: '#fff' } },
            error:   { iconTheme: { primary: '#EF4444', secondary: '#fff' } },
          }}
        />

        <Routes>
          <Route path="/login"         element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />

          <Route
            element={
              <RequireAuth>
                <AppLayout />
              </RequireAuth>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard"  element={<Dashboard />} />
            <Route path="/attendance" element={<AttendancePage />} />
            <Route path="/leaves"     element={<LeavesPage />} />
            <Route path="/holidays"   element={<HolidaysPage />} />
            <Route path="/profile"    element={<UserProfilePage />} />

            <Route path="/team/attendance" element={<RequireAuth roles={['admin','manager']}><AttendancePage teamView /></RequireAuth>} />
            <Route path="/team/leaves"     element={<RequireAuth roles={['admin','manager']}><LeavesPage teamView /></RequireAuth>} />
            <Route path="/team/overview"   element={<RequireAuth roles={['admin','manager']}><TeamOverviewPage /></RequireAuth>} />
            <Route path="/admin/verify-leave-balances" element={<RequireAuth roles={['admin','manager']}><AdminLeaveBalanceVerification /></RequireAuth>} />

            <Route path="/admin/users"         element={<RequireAuth roles={['admin']}><AdminUsersPage /></RequireAuth>} />
            <Route path="/admin/users/:userId" element={<RequireAuth roles={['admin']}><AdminUserEditPage /></RequireAuth>} />
            <Route path="/admin/holidays"      element={<RequireAuth roles={['admin']}><HolidaysPage /></RequireAuth>} />
            <Route path="/admin/leave-policies" element={<RequireAuth roles={['admin']}><AdminLeavePoliciesPage /></RequireAuth>} />
            <Route path="/admin/report"         element={<RequireAuth roles={['admin']}><AdminLossOfPayReportPage /></RequireAuth>} />
            <Route path="/admin/data-sync"      element={<RequireAuth roles={['admin']}><AdminDataSync /></RequireAuth>} />
          </Route>

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AuthInit>
    </BrowserRouter>
  );
}

function AdminUserEditPage() {
  const { userId } = require('react-router-dom').useParams();
  return <UserProfilePage viewUserId={userId} />;
}